import {
  queryTemplate,
  createCommonKnowledge,
  checkCommonKnowledge,
  getCommonKnowledges,
  resetCommonKnowledges,
  batchUpdateKnowledgeContent,
  editCommonKnowledge,
  getKnowledgeNotConfigStores,
} from '@knowledge/service/api/general';
import { useRequest } from 'ahooks';
import { useCallback, useEffect } from 'react';
import { getDefaultValueRule } from 'views/BootGuide/service';

let templateRefreshFn = () => {};

export const useStoreTemplateRefreshFn = () => {
  return useCallback((fn: () => void) => {
    fn && typeof fn === 'function' && (templateRefreshFn = fn);
  }, []);
};

export const useTemplate = () => {
  const { runAsync: fetchTemplate } = useRequest<
    CommonKnowledge.Template[],
    [Knowledge.KnowledgeType]
  >((definitionCode) => queryTemplate(definitionCode), {
    manual: true,
    cacheKey: 'template-kc',
  });

  return {
    fetchTemplate,
  } as const;
};

export const useBatchUpdateKnowledgeContent = () => {
  const { runAsync: batchUpdate, loading } = useRequest(
    (data) => batchUpdateKnowledgeContent(data),
    {
      manual: true,
      onSuccess: () => {
        templateRefreshFn();
      },
    },
  );

  return {
    batchUpdate,
    loading,
  };
};

// 这里需要对返回的知识列表做下分组，content和templateCode相同的为一组
export function groupBy(arr: any[] = []) {
  const keyMap = {};
  return Object.entries(
    arr?.reduce((acc, cur) => {
      const { templateCode, content } = cur;
      const key = `${templateCode}-${content}`;
      if (acc[key]) {
        acc[key].push(cur);
      } else {
        acc[key] = [cur];
      }

      if (!keyMap[key]) {
        keyMap[key] = {
          templateCode,
          content,
        };
      }
      return acc;
    }, {}),
  )?.map(([key, value]) => {
    return {
      ...keyMap[key],
      conditions: value,
    };
  });
}

export const getCommonTransferedKnowledges = (definitionCode: Knowledge.KnowledgeType) => {
  return getCommonKnowledges(definitionCode).then((res) => {
    return res.map(({ storeScope, ...others }) => {
      return {
        ...others,
        storeScope: {
          ...storeScope,
          storeScope: storeScope.storeScope.map(({ regionStoreScopes, ...others }: any) => {
            return {
              ...others,
              regionStoreScopes: regionStoreScopes.map(({ storeIds, ...others }: any) => {
                return {
                  ...others,
                  storeIds: storeIds?.map((id: any) => Number(id)),
                };
              }),
            };
          }),
        },
      };
    });
  });
};

export const useCommonKnowledgeList = (definitionCode: Knowledge.KnowledgeType) => {
  const { loading, data, refresh } = useRequest(
    () =>
      Promise.all([
        getCommonTransferedKnowledges(definitionCode),
        getKnowledgeNotConfigStores(definitionCode),
        getDefaultValueRule(),
      ]),
    {
      refreshDeps: [definitionCode],
    },
  );

  useEffect(() => {
    templateRefreshFn = refresh;
  }, [refresh]);

  return {
    data,
    loading,
    refresh,
  };
};

export const useCommonKnowledgeMutate = () => {
  const { runAsync: createKnowledge, loading: createLoading } = useRequest(
    (data) => createCommonKnowledge(data),
    {
      manual: true,
      onSuccess: () => {
        templateRefreshFn();
      },
    },
  );

  const { runAsync: editKnowledge, loading: editLoading } = useRequest(
    (data) => editCommonKnowledge(data),
    {
      manual: true,
      onSuccess: () => {
        templateRefreshFn();
      },
    },
  );

  const { runAsync: checkKnowledge, loading: checkLoading } = useRequest(
    (data) => checkCommonKnowledge(data),
    {
      manual: true,
    },
  );

  const { runAsync: resetKnowledges, loading: resetLoading } = useRequest(
    (data) => resetCommonKnowledges(data),
    {
      manual: true,
      onSuccess: () => {
        templateRefreshFn();
      },
    },
  );

  return {
    loading: createLoading || checkLoading || resetLoading || editLoading,
    createKnowledge,
    editKnowledge,
    checkKnowledge,
    resetKnowledges,
  };
};
